import React, { useState } from "react";
import {
  FiEdit2,
  FiMoreVertical,
  FiPaperclip,
  FiPrinter,
} from "react-icons/fi";
import "./CasePropertyTable.css";

const EMPTY_VALUE = "—";

const getValue = (value, fallback = EMPTY_VALUE) => {
  const normalizedValue = String(value ?? "").trim();
  return normalizedValue && normalizedValue.toLowerCase() !== "na"
    ? normalizedValue
    : fallback;
};

const formatDate = (value, includeTime = false, locale = "en-GB") => {
  if (!value || String(value).toLowerCase() === "na") {
    return { date: EMPTY_VALUE, time: "" };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: getValue(value), time: "" };
  }

  return {
    date: new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date),
    time: includeTime
      ? new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit",
        }).format(date)
      : "",
  };
};

const formatUpdatedDate = (value, locale = "en-GB") => {
  if (!value || String(value).toLowerCase() === "na") {
    return { date: EMPTY_VALUE, time: "" };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: getValue(value), time: "" };
  }

  return {
    date: new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      timeZone: "UTC",
      year: "numeric",
    }).format(date),
    time: new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
      timeZone: "UTC",
    }).format(date),
  };
};

const getLawDetails = (row, notEnteredLabel = "Not entered") => {
  const rawLaw = String(row.section_of_law ?? "").trim();

  if (!rawLaw || rawLaw.toLowerCase() === "na") {
    return { entries: [], summary: notEnteredLabel };
  }

  try {
    const parsedLaw = JSON.parse(rawLaw);

    if (
      !parsedLaw ||
      typeof parsedLaw !== "object" ||
      Array.isArray(parsedLaw)
    ) {
      return {
        entries: [{ act: rawLaw, sections: EMPTY_VALUE }],
        summary: rawLaw,
      };
    }

    const lawEntries = Object.entries(parsedLaw)
      .map(([act, sections]) => {
        const actName =
          act === "Others Acts" && String(row.specify_act ?? "").trim()
            ? row.specify_act
            : act;
        const sectionText = Array.isArray(sections)
          ? sections.filter(Boolean).join(", ")
          : getValue(sections, "");

        return {
          act: getValue(actName),
          sections: getValue(sectionText),
        };
      })
      .filter((entry) => entry.act !== EMPTY_VALUE);

    return {
      entries: lawEntries,
      summary: lawEntries.length
        ? lawEntries
            .map(({ act, sections }) => `${act}: ${sections}`)
            .join(" • ")
        : notEnteredLabel,
    };
  } catch {
    return {
      entries: [{ act: rawLaw, sections: EMPTY_VALUE }],
      summary: rawLaw,
    };
  }
};

const getPrice = (value, notEnteredLabel = "Not entered") => {
  const normalizedPrice = getValue(value, notEnteredLabel);

  return normalizedPrice === notEnteredLabel ||
    normalizedPrice.startsWith("₹")
    ? normalizedPrice
    : `₹${normalizedPrice}`;
};

function TableSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <tr className="case-register-skeleton" key={index}>
          {Array.from({ length: 7 }, (__, cellIndex) => (
            <td key={cellIndex}>
              <span className="case-register-skeleton__line" />
              <span className="case-register-skeleton__line case-register-skeleton__line--short" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default function CasePropertyTable({
  canEdit = false,
  canView = false,
  loading = false,
  onAttachment,
  onCustody,
  onEdit,
  onView,
  rows = [],
  translate,
}) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const text = (key, fallback) => {
    const translatedValue = translate?.(key);
    return translatedValue && translatedValue !== key
      ? translatedValue
      : fallback;
  };
  const notEnteredLabel = "Not entered";

  const runMenuAction = (callback, row) => {
    setOpenMenuId(null);
    callback?.(row);
  };
  const hasOverflowActions = canEdit || canView;

  return (
    <div className="case-register-table-wrap">
      <table className="case-register-table">
        <colgroup>
          <col className="case-register-column--property" />
          <col className="case-register-column--case" />
          <col className="case-register-column--details" />
          <col className="case-register-column--status" />
          <col className="case-register-column--next" />
          <col className="case-register-column--updated" />
          <col className="case-register-column--actions" />
        </colgroup>
        <thead>
          <tr>
            <th className="case-register-col--property">
              {text("property_id", "Property ID")}
            </th>
            <th className="case-register-col--case">
              {text("case_and_law", "Case & Law")}
            </th>
            <th className="case-register-col--details">
              {text("property_details", "Property Details")}
            </th>
            <th className="case-register-col--status">
              {text("custody_status", "Custody Status")}
            </th>
            <th className="case-register-col--next">
              {text("court_and_pendency", "Court & Pendency")}
            </th>
            <th className="case-register-col--updated">
              {text("updated_and_files", "Updated & Files")}
            </th>
            <th className="case-register-col--actions">
              {text("actions", "Actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? <TableSkeleton /> : null}

          {!loading && rows.length === 0 ? (
            <tr>
              <td className="case-register-empty" colSpan="7">
                <strong>
                  {text("no_case_properties", "No case properties found")}
                </strong>
                <span>
                  {text(
                    "change_search_filters",
                    "Try changing your search or filter criteria."
                  )}
                </span>
              </td>
            </tr>
          ) : null}

          {!loading
            ? rows.map((row) => {
                const seizedOn = formatDate(
                  row.date_of_seizure,
                  false,
                  "en-GB"
                );
                const updated = formatUpdatedDate(row.created_at, "en-GB");
                const quantity = getValue(row.quantity, notEnteredLabel);
                const unit = getValue(row.unit, "");
                const exhibit = getValue(row.exhibit_number, "");
                const lawDetails = getLawDetails(row, notEnteredLabel);
                const price = getPrice(row.price, notEnteredLabel);
                const nextCourtDate = formatDate(
                  row.next_court_date,
                  false,
                  "en-GB"
                ).date;
                const chartSheetDate = formatDate(
                  row.chart_sheet_date,
                  false,
                  "en-GB"
                ).date;
                const pendencyReason = getValue(
                  row.pendency_reason,
                  notEnteredLabel
                );

                return (
                  <tr key={row.id}>
                    <td className="case-register-property">
                      {canView ? (
                        <button
                          aria-label={`${text(
                            "view",
                            "View"
                          )} ${text(
                            "case_property_register",
                            "case property"
                          )} ${getValue(row.serial_number)}`}
                          className="case-register-property-id"
                          onClick={() => onView?.(row)}
                          type="button"
                        >
                          {getValue(row.serial_number)}
                        </button>
                      ) : (
                        <span className="case-register-property-id">
                          {getValue(row.serial_number)}
                        </span>
                      )}
                      {exhibit ? (
                        <span>
                          {text("exhibit", "Exhibit")} {exhibit}
                        </span>
                      ) : null}
                    </td>

                    <td className="case-register-case">
                      <strong>{getValue(row.guna_number)}</strong>
                      <span className="case-register-seized-line">
                        <span className="case-register-meta">
                          {text("seized", "Seized")}
                        </span>
                        <span>{seizedOn.date}</span>
                      </span>
                      <div
                        className="case-register-law-hover"
                        tabIndex={lawDetails.entries.length ? 0 : -1}
                      >
                        <span className="case-register-law-label">
                          {text("acts_sections", "Acts / sections")}
                        </span>
                        <span className="case-register-law">
                          {lawDetails.summary}
                        </span>
                        {lawDetails.entries.length ? (
                          <div
                            className="case-register-law-popover"
                            role="tooltip"
                          >
                            <table>
                              <thead>
                                <tr>
                                  <th>{text("act", "Act")}</th>
                                  <th>
                                    {text(
                                      "section_of_law",
                                      "Section of Law"
                                    )}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {lawDetails.entries.map((entry, index) => (
                                  <tr
                                    key={`${entry.act}-${entry.sections}-${index}`}
                                  >
                                    <td>{entry.act}</td>
                                    <td>{entry.sections}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                      </div>
                    </td>

                    <td className="case-register-details">
                      <span className="case-register-type">
                        {getValue(row.muddamaal_type, "Other")}
                      </span>
                      <span className="case-register-meta case-register-property-facts">
                        {text("quantity", "Quantity")}: {quantity}
                        {unit ? ` ${unit}` : ""}
                        <span aria-hidden="true"> • </span>
                        {text("price", "Price")}: {price}
                      </span>
                      <span
                        className="case-register-description"
                        title={getValue(row.description)}
                      >
                        {getValue(row.description)}
                      </span>
                    </td>

                    <td className="case-register-current-status">
                      <div className="case-register-status-content">
                        <button
                          className="case-register-status"
                          onClick={() => onCustody?.(row)}
                          type="button"
                        >
                          {getValue(
                            row.status_of_muddamaal,
                            "Status not entered"
                          )}
                        </button>
                        <span className="case-register-status-meta">
                          <span className="case-register-meta">
                            {text("updated", "Updated")}
                          </span>
                          <span>{updated.date}</span>
                        </span>
                        <button
                          className="case-register-link"
                          onClick={() => onCustody?.(row)}
                          type="button"
                        >
                          Custody Log
                        </button>
                      </div>
                    </td>

                    <td className="case-register-court">
                      <div className="case-register-court-content">
                        <div>
                          <span>{text("court_date", "Court date")}</span>
                          <strong>{nextCourtDate}</strong>
                        </div>
                        <div>
                          <span>{text("charge_sheet", "Charge sheet")}</span>
                          <strong>{chartSheetDate}</strong>
                        </div>
                        <div>
                          <span>{text("pendency", "Pendency")}</span>
                          <strong
                            className="case-register-pendency"
                            title={pendencyReason}
                          >
                            {pendencyReason}
                          </strong>
                        </div>
                      </div>
                    </td>

                    <td className="case-register-updated">
                      <strong>{updated.date}</strong>
                      {updated.time ? <span>{updated.time}</span> : null}
                      <button
                        className="case-register-attachment"
                        onClick={() => onAttachment?.(row)}
                        type="button"
                      >
                        <FiPaperclip /> Attachments
                      </button>
                    </td>

                    <td className="case-register-actions">
                      <div className="case-register-actions__inner">
                        {canView ? (
                          <button
                            className="case-register-view"
                            onClick={() => onView?.(row)}
                            type="button"
                          >
                            View
                          </button>
                        ) : null}
                        {hasOverflowActions ? (
                          <div
                            className="case-register-menu"
                            onBlur={(event) => {
                              if (
                                !event.currentTarget.contains(event.relatedTarget)
                              ) {
                                setOpenMenuId(null);
                              }
                            }}
                          >
                            <button
                              aria-expanded={openMenuId === row.id}
                              aria-haspopup="menu"
                              aria-label={`${text(
                                "more_actions",
                                "More actions"
                              )} ${getValue(row.serial_number)}`}
                              className="case-register-more"
                              onClick={() =>
                                setOpenMenuId((currentId) =>
                                  currentId === row.id ? null : row.id
                                )
                              }
                              type="button"
                            >
                              <FiMoreVertical />
                            </button>
                            {openMenuId === row.id ? (
                              <div
                                className="case-register-menu__panel"
                                role="menu"
                              >
                                {canEdit ? (
                                  <button
                                    onClick={() => runMenuAction(onEdit, row)}
                                    role="menuitem"
                                    type="button"
                                  >
                                    <FiEdit2 />{" "}
                                    Edit property
                                  </button>
                                ) : null}
                                {canView ? (
                                  <>
                                    <button
                                      onClick={() => runMenuAction(onView, row)}
                                      role="menuitem"
                                      type="button"
                                    >
                                      <FiPrinter />{" "}
                                      Print record
                                    </button>
                                    <button
                                      onClick={() =>
                                        runMenuAction(onAttachment, row)
                                      }
                                      role="menuitem"
                                      type="button"
                                    >
                                      <FiPaperclip />{" "}
                                      View attachments
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            : null}
        </tbody>
      </table>
    </div>
  );
}
